import { useState, useCallback, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../../../components/ui/dialog";
import { Button } from "../../../../../components/ui/button";
import { SampleUploadDropzone } from "./SampleUploadDropzone";
import { DetectedColumnsTable } from "./DetectedColumnsTable";
import { ColumnMappingTable } from "./ColumnMappingTable";
import { PreviewRowsTable } from "./PreviewRowsTable";
import { useSampleUpload } from "../../../../../hooks/useSampleUpload";
import type { AuditSession } from "../../../../../models/audit/auditSession";
import type {
  ParsedFilePreview,
  ParsedColumn,
  ColumnSelection,
  ColumnAttributeMapping,
  GeneratedAttributeType,
} from "../../../../../models/audit/sampleUpload";

type UploadStep = "upload" | "preview";

interface SampleUploadModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  auditSession: AuditSession;
  onSuccess: () => void;
}

export function SampleUploadModal({
  open,
  onOpenChange,
  auditSession,
  onSuccess,
}: SampleUploadModalProps) {
  const [step, setStep] = useState<UploadStep>("upload");
  const [parsedPreview, setParsedPreview] = useState<ParsedFilePreview | null>(
    null,
  );
  const [editedColumns, setEditedColumns] = useState<ParsedColumn[]>([]);
  const [matchColumnIndices, setMatchColumnIndices] = useState<Set<number>>(
    new Set(),
  );
  const [displayColumnIndices, setDisplayColumnIndices] = useState<Set<number>>(
    new Set(),
  );
  // For subsequent uploads: maps column index to attribute ID (or null if unmapped)
  const [columnMappings, setColumnMappings] = useState<
    Map<number, string | null>
  >(new Map());

  const {
    parseFile,
    confirmFirstUpload,
    confirmSubsequentUpload,
    parseLoading,
    confirmLoading,
  } = useSampleUpload({
    sessionId: auditSession.id,
  });

  // Get existing attributes from the procedure
  const existingAttributes = useMemo(
    () => auditSession.procedure?.attributes ?? [],
    [auditSession.procedure?.attributes],
  );

  const handleClose = useCallback(() => {
    setStep("upload");
    setParsedPreview(null);
    setEditedColumns([]);
    setMatchColumnIndices(new Set());
    setDisplayColumnIndices(new Set());
    setColumnMappings(new Map());
    onOpenChange(false);
  }, [onOpenChange]);

  const handleBack = useCallback(() => {
    setStep("upload");
    setParsedPreview(null);
    setEditedColumns([]);
    setMatchColumnIndices(new Set());
    setDisplayColumnIndices(new Set());
    setColumnMappings(new Map());
  }, []);

  const handleMatchColumnToggle = useCallback((columnIndex: number) => {
    setMatchColumnIndices((prev) => {
      const next = new Set(prev);
      if (next.has(columnIndex)) {
        next.delete(columnIndex);
      } else {
        next.add(columnIndex);
      }
      return next;
    });
  }, []);

  const handleDisplayColumnToggle = useCallback((columnIndex: number) => {
    setDisplayColumnIndices((prev) => {
      const next = new Set(prev);
      if (next.has(columnIndex)) {
        next.delete(columnIndex);
      } else {
        next.add(columnIndex);
      }
      return next;
    });
  }, []);

  const handleColumnNameChange = useCallback(
    (columnIndex: number, name: string) => {
      setEditedColumns((prev) =>
        prev.map((col) =>
          col.columnIndex === columnIndex ? { ...col, columnName: name } : col,
        ),
      );
    },
    [],
  );

  const handleDetectedTypeChange = useCallback(
    (columnIndex: number, detectedType: GeneratedAttributeType) => {
      setEditedColumns((prev) =>
        prev.map((col) =>
          col.columnIndex === columnIndex ? { ...col, detectedType } : col,
        ),
      );
    },
    [],
  );

  const handleMappingChange = useCallback(
    (columnIndex: number, attributeId: string | null) => {
      setColumnMappings((prev) => {
        const next = new Map(prev);
        next.set(columnIndex, attributeId);
        return next;
      });
    },
    [],
  );

  const handleFileSelect = useCallback(
    async (file: File) => {
      const preview = await parseFile(file);
      if (preview) {
        setParsedPreview(preview);
        // Create a mutable copy of columns for editing
        setEditedColumns(preview.columns.map((col) => ({ ...col })));

        // Default all columns to be displayed
        const allColumnIndices = new Set(
          preview.columns.map((col) => col.columnIndex),
        );
        setDisplayColumnIndices(allColumnIndices);

        if (preview.isFirstUpload) {
          // First upload: auto-select the first column detected as unique_id
          const uniqueIdColumn = preview.columns.find(
            (col) => col.detectedType === "unique_id",
          );
          const initialIndex =
            uniqueIdColumn?.columnIndex ?? preview.columns[0]?.columnIndex ?? 0;
          setMatchColumnIndices(new Set([initialIndex]));
        } else {
          // Subsequent upload: auto-map columns by matching names to existing display attributes
          const displayAttributes = existingAttributes.filter(
            (attr) => attr.sourceRowDisplay,
          );
          const initialMappings = new Map<number, string | null>();
          for (const col of preview.columns) {
            const matchingAttr = displayAttributes.find(
              (attr) =>
                attr.name.toLowerCase() === col.columnName.toLowerCase(),
            );
            initialMappings.set(col.columnIndex, matchingAttr?.id ?? null);
          }
          setColumnMappings(initialMappings);
        }

        setStep("preview");
      }
    },
    [parseFile, existingAttributes],
  );

  // Check if at least one column is mapped (for subsequent uploads)
  const hasValidMappings = useMemo(() => {
    for (const attrId of columnMappings.values()) {
      if (attrId !== null) return true;
    }
    return false;
  }, [columnMappings]);

  const handleConfirm = useCallback(async () => {
    if (!parsedPreview) return;

    let result;

    if (parsedPreview.isFirstUpload) {
      if (matchColumnIndices.size === 0) return;

      // First upload: create new procedure attributes
      const columnSelections: ColumnSelection[] = editedColumns.map(
        (column) => ({
          column_index: column.columnIndex,
          include: true,
          attribute_type: column.detectedType,
          attribute_name: column.columnName,
          source_row_display: displayColumnIndices.has(column.columnIndex),
        }),
      );

      result = await confirmFirstUpload(parsedPreview.fileId, columnSelections);
    } else {
      if (!hasValidMappings) return;

      // Subsequent upload: map columns to existing attributes
      const mappings: ColumnAttributeMapping[] = [];
      for (const [columnIndex, attributeId] of columnMappings.entries()) {
        if (attributeId !== null) {
          mappings.push({
            column_index: columnIndex,
            procedure_attribute_id: attributeId,
          });
        }
      }

      result = await confirmSubsequentUpload(parsedPreview.fileId, mappings);
    }

    if (result && result.status !== "error") {
      handleClose();
      onSuccess();
    }
  }, [
    parsedPreview,
    editedColumns,
    matchColumnIndices,
    displayColumnIndices,
    columnMappings,
    hasValidMappings,
    confirmFirstUpload,
    confirmSubsequentUpload,
    onSuccess,
    handleClose,
  ]);

  const isFirstUpload = parsedPreview?.isFirstUpload ?? true;
  const canConfirm = isFirstUpload
    ? matchColumnIndices.size > 0
    : hasValidMappings;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>
            {isFirstUpload ? "Upload Sample List" : "Add More Samples"}
          </DialogTitle>
          <DialogDescription>
            {step === "upload"
              ? "Upload a CSV or Excel file containing your sample list"
              : isFirstUpload
                ? "Confirm the columns from your file and select which to use for matching samples"
                : "Map the columns from your file to existing attributes"}
          </DialogDescription>
        </DialogHeader>

        <div className="py-4 flex-1 min-h-0 overflow-y-auto">
          {step === "upload" && (
            <SampleUploadDropzone
              onFileSelect={handleFileSelect}
              disabled={parseLoading}
              loading={parseLoading}
            />
          )}

          {step === "preview" && parsedPreview && (
            <div className="space-y-6">
              <div className="flex items-center justify-between text-sm text-stone-600">
                <span>
                  File:{" "}
                  <span className="font-medium">{parsedPreview.fileName}</span>
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleBack}
                  disabled={confirmLoading}
                >
                  Choose Different File
                </Button>
              </div>

              {isFirstUpload ? (
                <DetectedColumnsTable
                  columns={editedColumns}
                  matchColumnIndices={matchColumnIndices}
                  displayColumnIndices={displayColumnIndices}
                  onMatchColumnToggle={handleMatchColumnToggle}
                  onDisplayColumnToggle={handleDisplayColumnToggle}
                  onColumnNameChange={handleColumnNameChange}
                  onDetectedTypeChange={handleDetectedTypeChange}
                />
              ) : (
                <ColumnMappingTable
                  columns={editedColumns}
                  existingAttributes={existingAttributes}
                  columnMappings={columnMappings}
                  onMappingChange={handleMappingChange}
                />
              )}

              <PreviewRowsTable
                columns={editedColumns}
                previewRows={parsedPreview.previewRows}
                totalRowCount={parsedPreview.totalRowCount}
              />
            </div>
          )}
        </div>

        <DialogFooter className="flex-shrink-0">
          <Button
            variant="ghost"
            onClick={handleClose}
            disabled={confirmLoading}
          >
            Cancel
          </Button>
          {step === "preview" && (
            <Button
              onClick={handleConfirm}
              disabled={confirmLoading || !canConfirm}
              loading={confirmLoading}
            >
              {isFirstUpload ? "Upload Sample List" : "Add Samples"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
